import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
import { useAuth0 } from '@auth0/auth0-react';
import i18n from './i18n';
import { Toaster } from './components/ui/toaster';
import { useAuthStore } from './stores/authStore';
import { useEffect, useState } from 'react';
import { DoctorAuth0Provider } from './components/DoctorAuth0Provider';
import { ParentAuth0Provider } from './components/ParentAuth0Provider';
import { useTokenRefresh } from './hooks/useTokenRefresh';

// Pages
import { ParentLogin } from './pages/parent/Login';
import { ParentDashboard } from './pages/parent/Dashboard';
import { ParentPatientDetail } from './pages/parent/PatientDetail';
import { DoctorLogin } from './pages/doctor/Login';
import { DoctorDashboard } from './pages/doctor/Dashboard';
import { DoctorPatients } from './pages/doctor/Patients';
import { DoctorPatientDetail } from './pages/doctor/PatientDetail';
import { DoctorVaccines } from './pages/doctor/Vaccines';
import { DoctorSettings } from './pages/doctor/Settings';
import { SmartLogout } from './components/SmartLogout';
import { DoctorAuthCallback, ParentAuthCallback } from './pages/AuthCallback';

/**
 * Protected route wrapper that uses Auth0 from the parent provider
 */
function ProtectedRoute({ children, requiredType }: { children: React.ReactNode; requiredType: 'doctor' | 'parent' }) {
  const { isAuthenticated: isAuth0Authenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const { userType, setUserType } = useAuthStore();
  const [tokenReady, setTokenReady] = useState(() => !!localStorage.getItem('auth_token'));
  const [hasSetupToken, setHasSetupToken] = useState(false);

  // Ensure API client always has a valid refresh function while authenticated
  useTokenRefresh();

  // Sync userType from localStorage on mount (in case of page refresh)
  useEffect(() => {
    const storedUserType = localStorage.getItem('userType') as 'doctor' | 'parent' | null;
    if (storedUserType && !userType) {
      setUserType(storedUserType);
    }
  }, [userType, setUserType]);

  // Ensure we have a fresh access token ONCE before first render of protected content
  useEffect(() => {
    if (!isAuth0Authenticated || isLoading || hasSetupToken) {
      return;
    }

    let cancelled = false;

    const ensureToken = async () => {
      try {
        console.log('[ProtectedRoute] Ensuring fresh access token...');
        const token = await getAccessTokenSilently();
        localStorage.setItem('auth_token', token);
        console.log('[ProtectedRoute] Token stored successfully');
      } catch (error) {
        console.error('[ProtectedRoute] Failed to ensure access token:', error);
      } finally {
        if (!cancelled) {
          setTokenReady(true);
          setHasSetupToken(true);
        }
      }
    };

    ensureToken();

    return () => {
      cancelled = true;
    };
  }, [isAuth0Authenticated, isLoading, getAccessTokenSilently, hasSetupToken]);

  // Show loading while Auth0 is initializing OR waiting for initial token
  if (isLoading || (isAuth0Authenticated && !tokenReady)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  // Get userType from localStorage as fallback (for page refresh scenarios)
  const effectiveUserType = userType || localStorage.getItem('userType');
  const hasLocalToken = !!localStorage.getItem('auth_token');

  // Prefer Auth0 state, but allow local session as a fallback to avoid redirects
  // during provider remounts / transient Auth0 state glitches.
  if ((isAuth0Authenticated && effectiveUserType === requiredType) || (hasLocalToken && effectiveUserType === requiredType)) {
    return <>{children}</>;
  }

  // Redirect to the appropriate login page
  return <Navigate to={requiredType === 'doctor' ? '/doctor/login' : '/parent/login'} replace />;
}

function App() {
  // Custom root route logic: redirect to dashboard if authenticated, else to login
  function RootRedirect() {
    // IMPORTANT: Root route is not wrapped in an Auth0Provider.
    // Use localStorage as the source of truth for choosing portal.
    const userType = localStorage.getItem('userType') as 'doctor' | 'parent' | null;
    const userTypeDoctor = localStorage.getItem('userType_doctor');
    const userTypeParent = localStorage.getItem('userType_parent');
    const hasToken = !!localStorage.getItem('auth_token');

    const effective = userType || (userTypeDoctor === 'doctor' ? 'doctor' : userTypeParent === 'parent' ? 'parent' : null);

    if (hasToken && effective === 'doctor') {
      return <Navigate to="/doctor/dashboard" replace />;
    }
    if (hasToken && effective === 'parent') {
      return <Navigate to="/parent/dashboard" replace />;
    }
    return <Navigate to="/parent/login" replace />;
  }
  return (
    <I18nextProvider i18n={i18n}>
      <BrowserRouter>
        <Routes>
          {/* Auth callbacks - separate for each user type */}
          <Route path="/auth/callback/doctor" element={
            <DoctorAuth0Provider>
              <DoctorAuthCallback />
            </DoctorAuth0Provider>
          } />
          <Route path="/auth/callback/parent" element={
            <ParentAuth0Provider>
              <ParentAuthCallback />
            </ParentAuth0Provider>
          } />
          {/* Default route */}
          <Route path="/" element={<RootRedirect />} />
          {/* ...existing code... */}
          <Route path="/parent/login" element={
            <ParentAuth0Provider>
              <ParentLogin />
            </ParentAuth0Provider>
          } />
          <Route path="/doctor/login" element={
            <DoctorAuth0Provider>
              <DoctorLogin />
            </DoctorAuth0Provider>
          } />
          <Route path="/logout" element={<SmartLogout />} />
          {/* ...existing code... */}
          <Route path="/parent/dashboard" element={
            <ParentAuth0Provider>
              <ProtectedRoute requiredType="parent">
                <ParentDashboard />
              </ProtectedRoute>
            </ParentAuth0Provider>
          } />
          <Route path="/parent/patients/:id" element={
            <ParentAuth0Provider>
              <ProtectedRoute requiredType="parent">
                <ParentPatientDetail />
              </ProtectedRoute>
            </ParentAuth0Provider>
          } />
          <Route path="/doctor/dashboard" element={
            <DoctorAuth0Provider>
              <ProtectedRoute requiredType="doctor">
                <DoctorDashboard />
              </ProtectedRoute>
            </DoctorAuth0Provider>
          } />
          <Route path="/doctor/patients" element={
            <DoctorAuth0Provider>
              <ProtectedRoute requiredType="doctor">
                <DoctorPatients />
              </ProtectedRoute>
            </DoctorAuth0Provider>
          } />
          <Route path="/doctor/patients/:id" element={
            <DoctorAuth0Provider>
              <ProtectedRoute requiredType="doctor">
                <DoctorPatientDetail />
              </ProtectedRoute>
            </DoctorAuth0Provider>
          } />
          <Route path="/doctor/vaccines" element={
            <DoctorAuth0Provider>
              <ProtectedRoute requiredType="doctor">
                <DoctorVaccines />
              </ProtectedRoute>
            </DoctorAuth0Provider>
          } />
          <Route path="/doctor/settings" element={
            <DoctorAuth0Provider>
              <ProtectedRoute requiredType="doctor">
                <DoctorSettings />
              </ProtectedRoute>
            </DoctorAuth0Provider>
          } />
          {/* Catch all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster />
      </BrowserRouter>
    </I18nextProvider>
  );
}

export default App;
